'use client';

import { memo, useMemo } from 'react';
import type {
  DonateAction,
  FAQPage,
  Graph,
  NGO,
  ProfilePage,
  Thing,
} from 'schema-dts';

import { JsonLdGraph } from '@sola/ark-skippy/atoms/jsonLd/jsonLd.js';
import {
  buildFaqPageJsonLd,
  buildGiftCatalogSchema,
  buildHomeWebPageJsonLd,
  buildWebPageJsonLdFromPage,
  reactNodeToText,
} from '@sola/ark-skippy/utils/schema-utils.js';
import { PageResponseDto } from '@sola/schema/lib/contentful.js';

import { getRichText } from '../rich-text';

const extractFaqDataFromSections = (data: PageResponseDto) => {
  const faqItems: Array<{ question: string; answer: string | null }> = [];

  data.flexibleContent?.sections?.forEach((section) => {
    [...(section.column1 || []), ...(section.column2 || [])].forEach(
      (component) => {
        if (
          component.designComponent === 'accordion' &&
          component.designVariant === 'main'
        ) {
          // Get the referenced content for this accordion
          const referencedContent =
            data.referencedContentCollection?.items?.find(
              (item) => item?.sys?.id === component.contentEntry?.id,
            );

          if (
            referencedContent &&
            '__typename' in referencedContent &&
            referencedContent.__typename === 'FlexibleContent' &&
            referencedContent.itemsCollection?.items
          ) {
            referencedContent.itemsCollection.items.forEach((item) => {
              if (item.title && item.body) {
                const richTextContent = getRichText(item.body);
                const answerText = richTextContent?.content
                  ? reactNodeToText(richTextContent.content)
                  : null;

                faqItems.push({
                  question: item.title,
                  answer: answerText,
                });
              }
            });
          }
        }
      },
    );
  });

  return faqItems;
};

const buildSchemaGraph = (data: PageResponseDto): Graph | null => {
  const graph: Thing[] = [];
  const templateId = data.flexibleContent?.template?.templateId;

  if (templateId === 'bio') {
    const node: Partial<ProfilePage> = {
      ...buildWebPageJsonLdFromPage(data),
      '@type': 'ProfilePage',
    };
    graph.push(node as Thing);
  }

  // @TODO: Slug is not a reliable way to identify the home page. It can be changed in the CMS.
  if (data.slug === 'home') {
    const node: Partial<NGO> = {
      '@type': 'NGO',
      ...(buildHomeWebPageJsonLd() || {}),
    };
    graph.push(node as Thing);
  }

  if (templateId && ['giftCatalog', 'donate'].includes(templateId)) {
    const node: Partial<DonateAction> = {
      '@type': 'DonateAction',
      ...buildGiftCatalogSchema(),
    };
    graph.push(node as Thing);
  }

  // Generate FAQ Schema for pages with FAQ content
  const faqData = extractFaqDataFromSections(data);

  if (faqData.length > 0) {
    const faqJson = buildFaqPageJsonLd(faqData);

    if (
      faqJson.mainEntity &&
      Array.isArray(faqJson.mainEntity) &&
      faqJson.mainEntity.length > 0
    ) {
      const faqNode: Partial<FAQPage> = {
        '@type': 'FAQPage',
        ...faqJson,
      };
      graph.push(faqNode as Thing);
    }
  }

  return graph.length
    ? ({
        '@context': 'https://schema.org',
        '@graph': graph,
      } as const)
    : null;
};

type Props = { data: PageResponseDto };

const JsonLdBlockComponent = ({ data }: Props) => {
  const schemaGraph = useMemo(() => buildSchemaGraph(data), [data]);
  if (!schemaGraph) {
    return null;
  }

  return <JsonLdGraph item={schemaGraph} />;
};

export const JsonLdBlock = memo(JsonLdBlockComponent);
